import useIsMobile from '@/hooks/useIsMobile';
import { Avatar, Badge, Button, Popover, Spinner, Tooltip } from 'design-system';
import { Bell, UserRound as PersonIcon } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { Metallic as MetallicIcon } from '../../design-system/icons';
import getNotificationDisplay from '../../lib/notificationDisplay';
import getNotificationTarget from '../../lib/notificationTargets';
import {
  setNotificationIsRead,
  setUserNotificationReadRedux,
  setUserReadNotifications,
  setUserUnReadNotifications,
} from '../../redux/actions';
import { authHeader, fetch_request, protectedUrl } from '../../service/request';
import './Notifications.css';
import RequestActionButtons from './RequestActionButtons';
import RequestActionLabels from './RequestActionLabels';

const NOTIFICATIONS_TIMEOUT_MS = 4000;
const DEFAULT_TOTAL_PAGES = 1;

const normalizeNotificationsResponse = (data, fallbackPage = 1) => {
  if (Array.isArray(data)) {
    return { notifications: data, meta: { page: fallbackPage, total_pages: DEFAULT_TOTAL_PAGES } };
  }
  return {
    notifications: Array.isArray(data?.notifications) ? data.notifications : [],
    meta: data?.meta || { page: fallbackPage, total_pages: DEFAULT_TOTAL_PAGES },
  };
};

const Notifications = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const intl = useIntl();
  const location = useLocation();
  const [themeState, setThemeState] = useState(() => {
    if (typeof window === 'undefined') return null;
    return localStorage.getItem('theme');
  });
  const user = useSelector((state) => state.user.user);
  // Popover is controlled by DS component; no manual anchor state
  const isAuth = useSelector((state) => state.auth.isAuthorized);
  const [activeTab, setActiveTab] = useState('New');
  const rawUnread = useSelector((state) => state.user?.unreadNotifications);
  const rawRead = useSelector((state) => state.user?.readNotifications);
  const [unreadPage, setUnreadPage] = useState(1);
  const [readPage, setReadPage] = useState(1);
  const [unreadTotalPages, setUnreadTotalPages] = useState(DEFAULT_TOTAL_PAGES);
  const [readTotalPages, setReadTotalPages] = useState(DEFAULT_TOTAL_PAGES);
  const [unreadTotalCount, setUnreadTotalCount] = useState(null);
  const [isLoadingMoreUnread, setIsLoadingMoreUnread] = useState(false);
  const [isLoadingMoreRead, setIsLoadingMoreRead] = useState(false);

  const newNotifications = Array.isArray(rawUnread) ? rawUnread : [];
  const readNotifications = Array.isArray(rawRead) ? rawRead : [];
  const unreadRef = useRef(newNotifications);
  const readRef = useRef(readNotifications);

  const numericUnread = Number(rawUnread);
  const alertDotCount = Array.isArray(rawUnread)
    ? rawUnread.length
    : Number.isFinite(numericUnread)
      ? numericUnread
      : 0;
  const unreadCount = Number.isFinite(unreadTotalCount) ? unreadTotalCount : alertDotCount;
  const badgeCount = Math.min(unreadCount, 99);

  // Control DS Popover open state to avoid closing on internal clicks
  const [open, setOpen] = useState(false);
  const suppressCloseRef = useRef(false);

  const isMobile = useIsMobile();

  useEffect(() => {
    unreadRef.current = newNotifications;
  }, [newNotifications]);

  useEffect(() => {
    readRef.current = readNotifications;
  }, [readNotifications]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const updateTheme = () => {
      const stored = localStorage.getItem('theme');
      setThemeState(stored);
    };
    updateTheme();

    const handleStorage = (event) => {
      if (!event || event.key === 'theme') updateTheme();
    };

    window.addEventListener('storage', handleStorage);

    const observer = new MutationObserver(() => updateTheme());
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme'],
    });

    return () => {
      window.removeEventListener('storage', handleStorage);
      observer.disconnect();
    };
  }, []);

  const themeFromStorage = localStorage.getItem('theme');
  const isDark = themeFromStorage === 'dark';
  const isNotificationsRoute = location.pathname === '/notifications';

  const fetchReadNotifications = useCallback((page = 1, { append = false, onComplete } = {}) => {
    return fetch_request(`/api/notifications?read=true&page=${page}`, {
      method: 'GET',
      headers: { ...authHeader() },
      cache: 'no-store',
      timeoutMs: NOTIFICATIONS_TIMEOUT_MS,
    })
      .then((res) => res.json())
      .then((data) => {
        const normalized = normalizeNotificationsResponse(data, page);
        const current = append ? readRef.current : [];
        const nextNotifications = append
          ? [
              ...current,
              ...normalized.notifications.filter(
                (notification) => !current.some((item) => item.id === notification.id),
              ),
            ]
          : normalized.notifications;
        dispatch(setUserReadNotifications(nextNotifications));
        const totalPages = Number(normalized.meta?.total_pages) || DEFAULT_TOTAL_PAGES;
        const normalizedPage = Number(normalized.meta?.page) || page;
        setReadTotalPages(totalPages);
        setReadPage(normalizedPage);
      })
      .catch(console.log)
      .finally(() => {
        if (typeof onComplete === 'function') onComplete();
      });
  }, [dispatch]);

  const fetchUnReadNotifications = useCallback((page = 1, { append = false, onComplete } = {}) => {
    return fetch_request(`/api/notifications?read=false&page=${page}`, {
      method: 'GET',
      headers: { ...authHeader() },
      cache: 'no-store',
      timeoutMs: NOTIFICATIONS_TIMEOUT_MS,
    })
      .then((res) => res.json())
      .then((data) => {
        const normalized = normalizeNotificationsResponse(data, page);
        const current = append ? unreadRef.current : [];
        const nextNotifications = append
          ? [
              ...current,
              ...normalized.notifications.filter(
                (notification) => !current.some((item) => item.id === notification.id),
              ),
            ]
          : normalized.notifications;
        dispatch(setUserUnReadNotifications(nextNotifications));
        const totalPages = Number(normalized.meta?.total_pages) || DEFAULT_TOTAL_PAGES;
        const normalizedPage = Number(normalized.meta?.page) || page;
        const totalCount = Number(normalized.meta?.total_count);
        setUnreadTotalPages(totalPages);
        setUnreadPage(normalizedPage);
        setUnreadTotalCount(Number.isFinite(totalCount) ? totalCount : normalized.notifications.length);
      })
      .catch(console.log)
      .finally(() => {
        if (typeof onComplete === 'function') onComplete();
      });
  }, [dispatch]);

  useEffect(() => {
    if (!isAuth) return;
    setUnreadPage(1);
    setReadPage(1);
    setUnreadTotalPages(DEFAULT_TOTAL_PAGES);
    setReadTotalPages(DEFAULT_TOTAL_PAGES);
    setUnreadTotalCount(null);
  }, [user?.id, isAuth]);

  useEffect(() => {
    if (!Array.isArray(rawUnread)) return;
    if (rawUnread.length === 0) {
      if (unreadTotalCount !== 0) setUnreadTotalCount(0);
      return;
    }
    if (!Number.isFinite(unreadTotalCount) || unreadTotalCount < rawUnread.length) {
      setUnreadTotalCount(rawUnread.length);
    }
  }, [rawUnread, unreadTotalCount]);

  useEffect(() => {
    if (!isAuth) return;
    if (activeTab === 'Read') {
      fetchReadNotifications(readPage, {
        append: readPage > 1,
        onComplete: () => setIsLoadingMoreRead(false),
      });
      return;
    }
    fetchUnReadNotifications(unreadPage, {
      append: unreadPage > 1,
      onComplete: () => setIsLoadingMoreUnread(false),
    });
  }, [
    isAuth,
    activeTab,
    readPage,
    unreadPage,
    fetchReadNotifications,
    fetchUnReadNotifications,
  ]);

  const handleClose = () => {
    (newNotifications || []).forEach(makeNotificationRead);
    dispatch(setUserNotificationReadRedux());
  };

  const makeNotificationRead = (item) => {
    fetch_request(`/api/notifications/${item.id}`, {
      method: 'PUT',
      headers: { ...authHeader() },
      body: JSON.stringify({ read: true }),
    }).catch(console.log);
  };

  const handleNotificationClick = (item) => {
    const target = getNotificationTarget(item);
    if (!target) return;

    if (!item.read) {
      makeNotificationRead(item);
      dispatch(setNotificationIsRead(item.id));
    }
    navigate(target);
  };

  const getNotificationsSource = () => (activeTab === 'New' ? newNotifications : readNotifications);

  const formattedDateTime = (string) =>
    string
      ? new Date(string).toLocaleString('uk-UK', { dateStyle: 'short', timeStyle: 'short' })
      : '';

  const currentPage = activeTab === 'Read' ? readPage : unreadPage;
  const totalPages = activeTab === 'Read' ? readTotalPages : unreadTotalPages;
  const setPage = activeTab === 'Read' ? setReadPage : setUnreadPage;
  const isLoadingMore = activeTab === 'Read' ? isLoadingMoreRead : isLoadingMoreUnread;
  const showLoadMore = totalPages > 1 && currentPage < totalPages;

  // Responsive sizing for popover content
  const popoverContent = (
    <div
      style={{
        width: isMobile ? '75vw' : 420,
        maxWidth: isMobile ? '75vw' : 480,
        maxHeight: isMobile ? '70vh' : '70vh',
        overflowY: 'auto',
      }}
      // Prevent focus leaving trigger (avoids onBlur close) and mark suppression
      onMouseDown={(e) => {
        e.preventDefault();
        suppressCloseRef.current = true;
      }}
    >
      <div className="headline tabs" role="tablist" aria-label="Notifications tabs">
        <Button
          variant="text"
          size="sm"
          className={activeTab === 'New' ? 'active' : ''}
          aria-selected={activeTab === 'New'}
          onMouseDown={(e) => {
            e.preventDefault();
            suppressCloseRef.current = true;
          }}
          onClick={() => setActiveTab('New')}
        >
          <FormattedMessage id="new" />
        </Button>
        <Button
          variant="text"
          size="sm"
          className={activeTab === 'Read' ? 'active' : ''}
          aria-selected={activeTab === 'Read'}
          onMouseDown={(e) => {
            e.preventDefault();
            suppressCloseRef.current = true;
          }}
          onClick={() => {
            setActiveTab('Read');
          }}
        >
          <FormattedMessage id="read" />
        </Button>
        <Button
          type="link"
          size="sm"
          className="see-all-btn"
          onMouseDown={(e) => {
            e.preventDefault();
            suppressCloseRef.current = false;
          }}
          onClick={() => {
            setOpen(false);
            navigate('/notifications');
          }}
        >
          <FormattedMessage id="notifications_see_all" defaultMessage="See all" />
        </Button>
      </div>
      <div className="notifications-bar">
        {getNotificationsSource()?.length ? (
          getNotificationsSource().map((item, index) => {
            const itemClasses = ['item'];
            if (!item.read) itemClasses.push('item-unread');
            const display = getNotificationDisplay(item, intl);
            return (
              <div key={index} className={itemClasses.join(' ')}>
                <div className="notification-card" onClick={() => handleNotificationClick(item)}>
                  <Avatar
                    src={
                      item.extra_info?.user_avatar
                        ? protectedUrl(item.extra_info.user_avatar)
                        : undefined
                    }
                    icon={<PersonIcon aria-hidden />}
                    size={40}
                    alt=""
                  />
                  <div className="notification-copy">
                    {display.eyebrow && (
                      <div className="notification-eyebrow">{display.eyebrow}</div>
                    )}
                    <div className="text">{display.title}</div>
                    {display.body && <div className="notification-body">{display.body}</div>}
                  </div>
                </div>
                <div className="notification-footer">
                  <div className="notification-date-time">{formattedDateTime(item.created_at)}</div>
                  {item.show_button && <RequestActionButtons notification={item} inline />}
                  {(item.requestStatus || item.status) && (
                    <RequestActionLabels requestStatus={item.requestStatus || item.status} />
                  )}
                </div>
              </div>
            );
          })
        ) : (
          <div className="noItems-notification">
            <FormattedMessage id="empty_notification_message" />
          </div>
        )}
      </div>
      {showLoadMore && (
        <div className="notifications-pagination notifications-pagination-center">
          <Button
            variant="text"
            size="sm"
            disabled={isLoadingMore}
            onMouseDown={(e) => {
              e.preventDefault();
              suppressCloseRef.current = true;
            }}
            onClick={() => {
              if (activeTab === 'Read') {
                setIsLoadingMoreRead(true);
              } else {
                setIsLoadingMoreUnread(true);
              }
              setPage(currentPage + 1);
            }}
          >
            {isLoadingMore ? (
              <Spinner size={16} />
            ) : (
              <FormattedMessage id="notifications_load_more" defaultMessage="Load more" />
            )}
          </Button>
        </div>
      )}
    </div>
  );

  // Resolve bell variant for dark mode only (silver default, allow bronze via URL)
  let bellVariant = 'bronze';
  if (typeof window !== 'undefined') {
    const qp = new URLSearchParams(window.location.search).get('notifBell');
    if (qp === 'bronze' || qp === 'silver') bellVariant = qp;
  }

  const bellButton = (
    <Badge
      count={badgeCount}
      overflowCount={9}
      showZero={false}
      offset={[-4, 0]}
      size="sm"
      color="var(--accent-rose)"
    >
      <Button
        size="small"
        variant="filled"
        color="var(--surface-3)"
        shape="circle"
        style={{ color: 'var(--text)' }}
        icon={
          isDark ? (
            <MetallicIcon
              Icon={Bell}
              size={18}
              variant={bellVariant}
              strokeWidth={2.4}
              aria-hidden
            />
          ) : (
            <Bell size={18} strokeWidth={2} aria-hidden />
          )
        }
        aria-label={intl.formatMessage({
          id: 'aria_notifications',
          defaultMessage: 'notifications',
        })}
        onClick={() => {
          if (isMobile) {
            if (isNotificationsRoute) {
              if (typeof window !== 'undefined' && window.history.length > 1) {
                navigate(-1);
              } else {
                navigate('/sparks');
              }
              return;
            }
            navigate('/notifications');
            return;
          }
          if (isNotificationsRoute) {
            setOpen(false);
            return;
          }
          setOpen((v) => !v);
        }}
      />
    </Badge>
  );

  if (isMobile || isNotificationsRoute) {
    return <div className="notifications">{bellButton}</div>;
  }

  return (
    <div className="notifications">
      <Popover
        placement="bottomRight"
        arrow={false}
        open={open}
        onOpenChange={(v) => {
          if (isNotificationsRoute) {
            setOpen(false);
            return;
          }
          if (!v && suppressCloseRef.current) {
            // Ignore internal blur/clicks inside popover
            suppressCloseRef.current = false;
            return;
          }
          suppressCloseRef.current = false;
          setOpen(v);
          if (!v) handleClose();
        }}
        style={{ padding: '0px !important' }}
        content={popoverContent}
      >
        <Tooltip
          content={intl.formatMessage({
            id: 'aria_notifications',
            defaultMessage: 'notifications',
          })}
          placement="bottom"
        >
          {bellButton}
        </Tooltip>
      </Popover>
    </div>
  );
};

export default Notifications;
